import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { motion } from "motion/react"
import { useRequest } from "../../hooks/useRequest"
import { SbpBank, SbpPaymentResponse } from "../../types/steps/buy"
import Loader from "../../components/Loader"
import PaymentHoldBanner from "../../components/PaymentHoldBanner"
import CancelOrderDialog, { CancelOrderDialogHandle } from "../../components/CancelOrderDialog"
import { useIsKeyboardOpen } from "../../hooks/useIsKeyboardOpen"

const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

function useIsMobile() {
    const [mobile, setMobile] = useState(
        () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
    )

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)")
        const update = () => setMobile(mq.matches)
        mq.addEventListener("change", update)
        return () => mq.removeEventListener("change", update)
    }, [])

    return mobile
}

function pickBankUrl(bank: SbpBank) {
    const isIOS = /iPad|iPhone|iPod/i.test(navigator.userAgent)
    if (isIOS) return bank.iosUrl || bank.androidUrl
    return bank.androidUrl || bank.iosUrl
}

type SbpOrderPaymentProps = {
    orderId: string
    orderNumber: string
    qrImage?: string
    initialSbpBanks?: SbpBank[]
    orderCreatedAt?: string
    orderExpiredAt?: string
    onClose?: () => void
    onPaid?: () => void
    onExpired?: () => void
    asModal?: boolean
    onCancelOrder?: () => void
}

export default function SbpOrderPayment({
    orderId,
    orderNumber,
    qrImage,
    initialSbpBanks,
    orderCreatedAt,
    orderExpiredAt,
    onClose,
    onPaid,
    onExpired,
    asModal = false,
    onCancelOrder,
}: SbpOrderPaymentProps) {
    const { request } = useRequest()
    const isMobile = useIsMobile()
    const [banksLoading, setBanksLoading] = useState(isMobile && !initialSbpBanks?.length)
    const [banks, setBanks] = useState<SbpBank[]>(initialSbpBanks ?? [])
    const [bankQuery, setBankQuery] = useState("")
    const [holdCreatedAt, setHoldCreatedAt] = useState(orderCreatedAt)
    const [holdExpiredAt, setHoldExpiredAt] = useState(orderExpiredAt)
    const confirmRef = useRef<CancelOrderDialogHandle>(null)
    const isKeyboardOpen = useIsKeyboardOpen()

    const filteredBanks = useMemo(() => {
        const q = bankQuery.trim().toLowerCase()
        if (!q) return banks
        return banks.filter(bank => bank.bank_name.toLowerCase().includes(q))
    }, [banks, bankQuery])

    useEffect(() => {
        if (!orderId) return
        request<{ created_at?: string; expired_at?: string }>("GET", `user/order/${orderId}`)
            .then(order => {
                if (order.created_at) setHoldCreatedAt(order.created_at)
                if (order.expired_at) setHoldExpiredAt(order.expired_at)
            })
            .catch(() => {})
    }, [orderId])

    useEffect(() => {
        if (!isMobile || !orderId || initialSbpBanks?.length) return
        setBanksLoading(true)
        request<SbpPaymentResponse>("GET", `user/order/${orderId}/payment`, undefined, { disableError: true })
            .then(data => {
                if (data.sbp_banks?.length) setBanks(data.sbp_banks)
            })
            .catch(() => {})
            .finally(() => setBanksLoading(false))
    }, [isMobile, orderId, initialSbpBanks])

    const onExitClick = () => {
        confirmRef.current?.open({
            orderId,
            unpaid: true,
            onCancel: async () => {
                await request("DELETE", `user/order/${orderId}`)
            },
            onSuccess: () => {
                onCancelOrder?.()
                onClose?.()
            },
        })
    }

    const openBank = (bank: SbpBank) => {
        const url = pickBankUrl(bank)
        if (url) window.location.href = url
    }

    useEffect(() => {
        const intervalId = setInterval(async () => {
            try {
                const data = await request<{ order_status: string }>("POST", `user/order/${orderNumber}/payment/checksbp`)
                if (data.order_status === "paid") {
                    onPaid?.()
                } else if (data.order_status === "expired") {
                    onExpired?.()
                }
            } catch {
                /* ignore */
            }
        }, 5000)

        return () => clearInterval(intervalId)
    }, [orderNumber, onPaid, onExpired])

    const mobileBankList = (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
            <p className="shrink-0 text-center text-[14px] leading-snug text-white/70" style={{ fontFamily: BODY, fontWeight: 500 }}>
                Выберите банк для оплаты через СБП
            </p>
            {banks.length > 0 && (
                <div className="relative shrink-0">
                    <input
                        type="text"
                        inputMode="search"
                        autoComplete="off"
                        value={bankQuery}
                        onChange={e => setBankQuery(e.target.value)}
                        placeholder="Поиск банка"
                        className="h-[44px] w-full rounded-[12px] border border-white/10 bg-white/[0.06] pl-4 pr-10 text-[15px] text-white placeholder:text-white/35 focus:border-[#349be6]/50 focus:outline-none"
                        style={{ fontFamily: BODY, fontWeight: 500 }}
                    />
                    {bankQuery && (
                        <button
                            type="button"
                            onClick={() => setBankQuery("")}
                            className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer text-[13px] text-white/40"
                            style={{ fontFamily: MONO, fontWeight: 700 }}
                            aria-label="Очистить поиск">
                            ✕
                        </button>
                    )}
                </div>
            )}
            {banksLoading ? (
                <div className="flex flex-1 items-center justify-center">
                    <Loader />
                </div>
            ) : banks.length > 0 ? (
                filteredBanks.length > 0 ? (
                    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-0.5 pb-1">
                        {filteredBanks.map(bank => (
                            <motion.button
                                key={bank.bank_name}
                                type="button"
                                whileTap={{ scale: 0.98 }}
                                onClick={() => openBank(bank)}
                                className="flex w-full shrink-0 items-center gap-3 rounded-[14px] border border-white/10 bg-white/[0.05] px-3 py-2.5 text-left">
                                {bank.logo_url ? (
                                    <img src={bank.logo_url} alt="" className="size-10 shrink-0 rounded-[10px] bg-white object-contain p-1" />
                                ) : (
                                    <div className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-white/10 text-[18px]">🏦</div>
                                )}
                                <span className="min-w-0 flex-1 text-[14px] text-white" style={{ fontFamily: BODY, fontWeight: 600 }}>
                                    {bank.bank_name}
                                </span>
                                <span className="shrink-0 text-white/30" style={{ fontFamily: MONO, fontWeight: 700 }}>
                                    →
                                </span>
                            </motion.button>
                        ))}
                    </div>
                ) : (
                    <p className="flex flex-1 items-center justify-center py-6 text-center text-[14px] text-white/50" style={{ fontFamily: BODY, fontWeight: 500 }}>
                        Банк не найден
                    </p>
                )
            ) : (
                <p className="flex flex-1 items-center justify-center py-6 text-center text-[14px] text-white/50" style={{ fontFamily: BODY, fontWeight: 500 }}>
                    Список банков временно недоступен
                </p>
            )}
        </div>
    )

    const content = (
        <div className="flex h-full min-h-0 flex-1 flex-col gap-4 px-5 py-5 md:gap-6 md:py-6">
            <PaymentHoldBanner createdAt={holdCreatedAt} expiredAt={holdExpiredAt} className="w-full shrink-0" />

            {isMobile ? (
                mobileBankList
            ) : (
                <div className="flex flex-col items-center gap-6">
                    <p className="text-center text-[14px] leading-snug text-white/70" style={{ fontFamily: BODY, fontWeight: 500 }}>
                        Для оплаты откройте камеру на смартфоне и наведите на QR-код. Далее выберите свой банк для оплаты билета.
                    </p>
                    {qrImage && (
                        <img src={`data:image/png;base64,${qrImage}`} alt="QR-код для оплаты через СБП" className="size-[220px] max-w-full" />
                    )}
                </div>
            )}

            <div className={`mt-auto flex shrink-0 flex-col gap-4 border-t border-white/10 pt-4 ${isKeyboardOpen ? "pb-1" : "pb-[max(0px,var(--safe-bottom))]"}`}>
                <div className="flex flex-col gap-1.5">
                    <div className="text-center text-[16px] text-white" style={{ fontFamily: BODY, fontWeight: 700 }}>
                        Ждём ответ от СБП...
                    </div>
                    <div className="text-center text-[14px] text-white/50" style={{ fontFamily: BODY, fontWeight: 500 }}>
                        Это может занять некоторое время. Пожалуйста, не закрывайте страницу
                    </div>
                </div>

                {(onClose || onCancelOrder !== undefined) && (
                    <button
                        type="button"
                        onClick={onExitClick}
                        className="h-[50px] w-full cursor-pointer rounded-[14px] bg-white/10 text-[15px] text-white"
                        style={{ fontFamily: BODY, fontWeight: 700 }}>
                        Отменить покупку
                    </button>
                )}
            </div>
            <CancelOrderDialog ref={confirmRef} />
        </div>
    )

    if (!asModal) return content

    return createPortal(
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[250] flex items-end justify-center md:items-center"
            style={{ background: "rgba(10,18,42,0.85)", backdropFilter: "blur(8px)" }}
            onClick={onClose}>
            <motion.div
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="flex h-[92svh] max-h-[92svh] w-full flex-col overflow-hidden rounded-t-[24px] md:h-auto md:max-h-[92svh] md:max-w-md md:rounded-[24px]"
                style={{ background: "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)" }}
                onClick={e => e.stopPropagation()}>
                {content}
            </motion.div>
        </motion.div>,
        document.body,
    )
}
